import { HttpClient, HttpParams } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { BehaviorSubject, tap } from 'rxjs'
import { UserModel } from '../../../core/models/user.model'
import { PaginatedResponseModel } from '../../../core/models/paginated-response.model'
import { PaginatedRequestModel } from '../../../core/models/paginated-request.model'
import { StandardApiResponseModel } from '../../../core/models/standard-api-response.model'
import { environment } from '../../../../environments/environment'

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private http = inject(HttpClient)
  private planifyApiUrl = environment.planifyApiUrl

  private user = new BehaviorSubject<UserModel | null>(null)
  private users = new BehaviorSubject<UserModel[]>([])
  private total = new BehaviorSubject<number>(0)
  private page = new BehaviorSubject<number>(1)
  private loading = new BehaviorSubject<boolean>(false)
  private size = new BehaviorSubject<number>(10)
  private search = new BehaviorSubject<string>('')

  public users$ = this.users.asObservable()
  public total$ = this.total.asObservable()
  public page$ = this.page.asObservable()
  public loading$ = this.loading.asObservable()

  public getUsers(options: PaginatedRequestModel = {}) {
    this.loading.next(true)
    return this.http
      .post<
        PaginatedResponseModel<UserModel>
      >(`${this.planifyApiUrl}/users/list`, options)
      .pipe(
        tap((res) => {
          this.users.next(res.data)
          this.total.next(res.meta.total)
          this.page.next(res.meta.current_page)
          this.size.next(res.meta.per_page)
          this.search.next(options.search ?? '')
          this.loading.next(false)
        })
      )
  }
  getUsersWithFilters(
    filters: Partial<{
      departmentsCodes: string[] | null
      societyKeys: string[] | null
      search: string | null
      productsNames?: string[] | null
    }>
  ) {
    return this.http.post<UserModel[]>(
      `${this.planifyApiUrl}/users/filters`,
      filters
    )
  }

  getUser(userId: number) {
    return this.http
      .get<UserModel>(`${this.planifyApiUrl}/users/${userId}`)
      .pipe(
        tap((user) => {
          this.user.next(user)
        })
      )
  }

  getForSelect() {
    // L'endpoint renvoie une forme allégée { label, value } (cf. UserSelectResource côté API).
    return this.http.get<{ label: string; value: number }[]>(
      `${this.planifyApiUrl}/users/select`
    )
  }

  getClosest(departmentCode: string, productsNames: string[]) {
    return this.http.post<UserModel[]>(
      `${this.planifyApiUrl}/users/departments/closest`,
      {
        departmentCode,
        productsNames,
      }
    )
  }

  /**
   * Récupère les techniciens les plus proches d'un point donné
   * en utilisant les distances routières (Valhalla)
   */
  getClosestByRoute(params: {
    workSiteLatitude: number
    workSiteLongitude: number
    number?: number
    maxDistance?: number
    productsNames?: string[]
  }) {
    return this.http.post<{
      technicians: {
        technician: UserModel
        distance_km: number
        duration_min: number | null
        distance_type: 'route' | 'haversine'
      }[]
      distance_type: 'route' | 'haversine' | 'none'
    }>(`${this.planifyApiUrl}/users/closest-by-route`, params)
  }

  getAvailabilities(data: any) {
    return this.http.post<any>(
      `${this.planifyApiUrl}/users/availabilities`,
      data
    )
  }

  public refreshUsers() {
    this.getUsers({
      page: this.page.value,
      size: this.size.value,
      search: this.search.value,
    }).subscribe()
  }

  public updateUser(user: UserModel, userId: number) {
    return this.http.put<StandardApiResponseModel<unknown>>(
      `${this.planifyApiUrl}/users/${userId}`,
      user
    )
  }
}
